import { ArrowRight, KeyRound, LockKeyhole, ShieldCheck, Sparkles, UserRoundCheck } from "lucide-react";
import { useEffect } from "react";
import { SupposeLogo, SupposeMark } from "../components/Brand";
import { Button } from "../components/UI";

type AuthErrorCode = "access_denied" | "invalid_request" | "unavailable";

function authErrorCode(): AuthErrorCode | null {
  const value = new URLSearchParams(window.location.search).get("auth_error");
  return value === "access_denied" || value === "invalid_request" || value === "unavailable" ? value : null;
}

function errorCopy(code: AuthErrorCode): { eyebrow: string; title: string; message: string } {
  if (code === "access_denied") {
    return {
      eyebrow: "Access not granted",
      title: "This account is not an administrator",
      message: "An owner must add this existing Suppose account to the active admin list before it can enter.",
    };
  }
  if (code === "unavailable") {
    return {
      eyebrow: "Sign in unavailable",
      title: "The secure handoff could not finish",
      message: "Your account was not changed. Try the connection again in a moment.",
    };
  }
  return {
    eyebrow: "Request expired",
    title: "Start a fresh secure connection",
    message: "The one-time sign-in request was invalid, expired, or already used.",
  };
}

export function LoginPage() {
  const error = authErrorCode();
  useEffect(() => {
    if (!error) window.location.replace("/api/auth/start");
  }, [error]);
  const copy = error ? errorCopy(error) : null;

  return (
    <main className="login-page">
      <div className="login-ambient" aria-hidden="true">
        <span className="orbit orbit-one" /><span className="orbit orbit-two" /><span className="orbit orbit-three" />
        <span className="floating-node node-one" /><span className="floating-node node-two" /><span className="floating-node node-three" /><span className="floating-node node-four" />
      </div>
      <header className="login-brand"><SupposeLogo /><span>private operations</span></header>
      <section className="login-layout">
        <div className="login-card-wrap">
          <div className="login-card">
            <div className="login-card-mark"><SupposeMark /></div>
            {copy ? (
              <div className="login-success">
                <span className="verification-error"><LockKeyhole size={25} /></span>
                <div className="eyebrow">{copy.eyebrow}</div>
                <h2>{copy.title}</h2>
                <p>{copy.message}</p>
                <Button variant="primary" size="large" onClick={() => window.location.assign("/api/auth/start")}>Try again <ArrowRight size={17} /></Button>
              </div>
            ) : (
              <div className="login-success" role="status">
                <span className="verification-thinking"><span className="thinking-dots"><i /><i /><i /></span></span>
                <div className="eyebrow">Suppose account</div>
                <h2>Connecting your secure session</h2>
                <p>If you are already signed in to Suppose, you will return here automatically. Otherwise, use the normal Suppose sign-in.</p>
                <div className="login-security"><KeyRound size={14} /> No separate admin password or email link.</div>
              </div>
            )}
          </div>
          <div className="login-caption"><span className="status-dot" /> Allowlisted accounts only</div>
        </div>
        <div className="login-story">
          <div className="eyebrow"><Sparkles size={14} /> The quiet room behind the Boards</div>
          <h1>See the whole thought <em>taking shape.</em></h1>
          <p>Suppose admin brings customers, Boards, runtime health, and operational decisions into one thoughtful place.</p>
          <div className="login-principles">
            <div><span><ShieldCheck size={18} /></span><strong>Deliberate access</strong><p>Selected operators only, with every sensitive action recorded.</p></div>
            <div><span><UserRoundCheck size={18} /></span><strong>One Suppose identity</strong><p>Your existing account proves who you are. Admin roles remain separately controlled.</p></div>
          </div>
        </div>
      </section>
      <footer className="login-footer"><span>suppose_admin</span><span>Internal use only</span></footer>
    </main>
  );
}
